import { Component, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { tryAutoReloadOnChunkError } from "@/lib/appUpdateNotifier";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

class ErrorBoundaryClass extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    if (tryAutoReloadOnChunkError(error)) return;
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return <ErrorBoundaryFallback />;
    }

    return this.props.children;
  }
}

function ErrorBoundaryFallback() {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-5 bg-background px-6 text-center">
      <span className="text-5xl">🔥</span>
      <div className="space-y-1">
        <p className="text-base font-semibold text-foreground">
          {t("error.heading")}
        </p>
        <p className="text-sm text-muted-foreground">
          {t("error.subheading")}
        </p>
      </div>
      <button
        onClick={() => window.location.reload()}
        className="rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground transition-transform hover:scale-105 active:scale-95"
      >
        {t("button.reload")}
      </button>
    </div>
  );
}

export default ErrorBoundaryClass;
